import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { HeaderSorterComponent } from '../../../common/tables/header-sorter/header-sorter.component';
import { CommonModule } from '@angular/common';
import { ErrorCode } from '../../../../../shared/models/error-code/error-code';
import { ErrorCodeQuery } from '../../../../../shared/models/error-code/error-code-query';
import { CommonUtils, getPropertyName } from '../../../../common/common-utils';
import { SortingConfiguration } from '../../../../../shared/internal/sorting-configuration';
import { Policy } from '../../../../../shared/models/info/policy';
import { DeleteIconComponent } from '../../../icons/delete-icon/delete-icon.component';
import { ErrorCodeTableSearchComponent } from '../../../search/error-code/error-code-table-search/error-code-table-search.component';
import { CheckIconComponent } from '../../../icons/check-icon/check-icon.component';
import { EnabledByPolicyDirective } from '../../../../directives/enabled-by-policy.directive';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { ErrorCodeWithQuey } from '../../../../../shared/models/error-code/error-code-with-query';
import { NoteIconComponent } from '../../../icons/note-icon/note-icon.component';
import { DecisionModalComponent } from '../../../modals/decision-modal/decision-modal.component';
import { Decision } from '../../../../../shared/models/internal/decision';
import { RestoreIconComponent } from '../../../icons/restore-icon/restore-icon.component';
import { ScrollableBase } from '../../../base/scrollable-base';

@Component({
  selector: 'app-error-code-table',
  imports: [
    HeaderSorterComponent,
    CommonModule,
    DeleteIconComponent,
    ErrorCodeTableSearchComponent,
    EnabledByPolicyDirective,
    AgentActionDirective,
    CheckIconComponent,
    NoteIconComponent,
    DecisionModalComponent,
    RestoreIconComponent,
  ],
  templateUrl: './error-code-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './error-code-table.component.scss',
})
export class ErrorCodeTableComponent extends ScrollableBase<ErrorCodeQuery> {
  @Input() public errorCodes?: ErrorCode[];
  @Input() public softDeleteDisabled = false;
  @Input() public restoreDisabled = false;
  @Input() public hardDeleteDisabled = false;
  @Input() public selectedElementId?: string;
  @Input() public recyclingModeEnabled = false;
  @Output() public sortByFieldChanged: EventEmitter<ErrorCodeQuery> = new EventEmitter<ErrorCodeQuery>();
  @Output() public errorCodeClicked: EventEmitter<string> = new EventEmitter<string>();
  @Output() public searchTriggered: EventEmitter<ErrorCodeQuery> = new EventEmitter<ErrorCodeQuery>();
  @Output() public softDeleteErrorCode: EventEmitter<ErrorCodeWithQuey> = new EventEmitter<ErrorCodeWithQuey>();
  @Output() public hardDeleteErrorCode: EventEmitter<ErrorCodeWithQuey> = new EventEmitter<ErrorCodeWithQuey>();
  @Output() public restoreErrorCode: EventEmitter<ErrorCodeWithQuey> = new EventEmitter<ErrorCodeWithQuey>();

  public ecToRemove?: ErrorCode;
  public getPropertyName = getPropertyName<ErrorCode>;
  public sortingConfiguration = new SortingConfiguration(this.getPropertyName('code'));
  public policy = Policy;
  public commonUtils = CommonUtils;

  public sortBy(field: string): void {
    const sortByField = this.sortingConfiguration.sortBy(field);
    if (this.query) {
      this.query.orderBy = sortByField.field;
      this.query.orderByDescending = sortByField.orderByDescending;
      this.sortByFieldChanged.emit(this.query);
    }
  }

  public onErrorCodeClicked(id?: string): void {
    this.errorCodeClicked.emit(id);
  }

  public onSearchTriggered(query: ErrorCodeQuery): void {
    this.query = query;
    const sortByField = this.sortingConfiguration.SortByField;
    if (sortByField) {
      this.query.orderBy = sortByField.field;
      this.query.orderByDescending = sortByField.orderByDescending;
    }
    this.searchTriggered.emit(query);
  }

  public onSoftDeleteDecision(decision: Decision): void {
    if (decision.result && this.ecToRemove) {
      this.softDeleteErrorCode.emit({ errorCode: this.ecToRemove, query: this.query });
    }
  }

  public onHardDeleteDecision(decision: Decision): void {
    if (decision.result && this.ecToRemove) {
      this.hardDeleteErrorCode.emit({ errorCode: this.ecToRemove, query: this.query });
    }
  }

  public showDecisionModal(id?: string, ecValue?: string): void {
    this.ecToRemove = { id: id, displayId: ecValue };
  }

  public onRestore(errorCode: ErrorCode): void {
    this.restoreErrorCode.emit({ errorCode: errorCode, query: this.query });
  }

  public isSelected(id: string | undefined): boolean {
    return this.selectedElementId === id;
  }
}
